import { Briefcase, CheckSquare, EyeOff, Filter, Globe, Search, Square, Workflow, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import CandidateCard from "../components/candidates/CandidateCard";
import BulkActionsBar from "../components/candidates/BulkActionsBar";
import SavedViewsBar, { CollapsibleFilters } from "../components/common/SavedViewsBar";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { saveQueue } from "../lib/callQueue";
import { hasRole } from "../lib/roles";

// Real candidate statuses (can be multi-selected)
const STATUS_OPTIONS = [
    { key: "new", label: "Nouveaux" },
    { key: "prerec_done", label: "Pré-rec fait" },
    { key: "immersion_scheduled", label: "Immersion" },
    { key: "immersion_done", label: "Validation attente" },
    { key: "onboarding", label: "Onboarding" },
    { key: "rejected", label: "Refusés" },
];

// Pseudo-statuses are EXCLUSIVE (heuristics, not real statuses). Selecting one
// clears the multi-status selection and vice-versa.
const PSEUDO_FILTERS = [
    { key: "urgent", label: "Appels urgents" },
    { key: "to_recall", label: "À rappeler" },
];

const STATUS_LABEL_LONG = {
    "": "Tous les candidats",
    urgent: "Appels urgents",
    to_recall: "À rappeler (pas de réponse)",
    new: "Nouveaux candidats",
    prerec_done: "Pré-recrutement terminé",
    immersion_scheduled: "Immersions programmées",
    immersion_done: "En attente de validation",
    onboarding: "Onboarding en cours",
    rejected: "Candidats refusés",
};

const OFFER_STATUS_TONE = {
    "Publiée": "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Brouillon": "bg-amber-50 text-amber-800 border-amber-200",
    "Archivée": "bg-slate-100 text-slate-600 border-slate-200",
};

const SOURCES = {
    SITE: "Site Shariti",
    LAYAN: "Layan · Site d'annonce",
};

// Parse a comma-separated query param into a Set of trimmed non-empty values.
function parseSetParam(raw) {
    if (!raw) return new Set();
    return new Set(raw.split(",").map((s) => s.trim()).filter(Boolean));
}

function setParamFromSet(next, key, set) {
    if (set.size === 0) next.delete(key);
    else next.set(key, Array.from(set).join(","));
}

export default function Candidates({ kind = "all" } = {}) {
    // kind = "all" | "pepites" | "alertes"
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isAdmin = hasRole(user, "admin");
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);

    // "Cacher les déjà traités" toggle — when ON (default), candidates whose
    // status is past the current user's role action point are hidden from the
    // queue and folded behind a "Voir les déjà traités" button. Saves the
    // pre-recruiter from clicking through candidates who already had their
    // immersion scheduled. Persisted in localStorage so it survives reload.
    const [showDone, setShowDone] = useState(() => {
        try { return localStorage.getItem("prerec_show_done") === "1"; }
        catch { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem("prerec_show_done", showDone ? "1" : "0"); } catch { /* ignore */ }
    }, [showDone]);

    // === MULTI-SELECTION (bulk vivier) ===
    // Toggled by a "Sélection groupée" button next to the "Masquer traités"
    // toggle. When ON: each card renders a checkbox and a sticky bottom bar
    // appears as soon as one row is ticked. Selection clears automatically
    // when the user navigates filters or toggles selection mode OFF.
    const [selectMode, setSelectMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    function toggleOne(id, checked) {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    }
    function clearSelection() { setSelectedIds(new Set()); }
    function toggleSelectMode() {
        setSelectMode((on) => {
            if (on) clearSelection();
            return !on;
        });
    }

    // === FILTER STATE ===
    // Pseudo filters (urgent / to_recall) are exclusive.
    const rawStatus = searchParams.get("status") || "";
    const isPseudo = rawStatus === "urgent" || rawStatus === "to_recall";
    const pseudoFilter = isPseudo ? rawStatus : "";
    const statusValues = useMemo(
        () => (isPseudo ? new Set() : parseSetParam(rawStatus)),
        [rawStatus, isPseudo],
    );

    const sourceValues = useMemo(
        () => parseSetParam(searchParams.get("source") || ""),
        [searchParams],
    );

    const [search, setSearch] = useState(searchParams.get("q") || "");
    const [scopeActive, setScopeActive] = useState(
        searchParams.get("scope") !== "all",
    );
    // Admin-only "Afficher les candidats test" checkbox. Persisted in the
    // URL as `show_test=1`. When ON, `is_test=True` candidates surface in
    // the list with their 🧪 badge — used to reach a false-positive
    // auto-flag and toggle it back OFF from the profile.
    const [showTest, setShowTest] = useState(
        searchParams.get("show_test") === "1",
    );
    const offerIdsRaw = searchParams.get("offer_id");
    const offerIds = useMemo(() => {
        if (!offerIdsRaw) return new Set();
        return new Set(
            offerIdsRaw.split(",").map((s) => s.trim()).filter(Boolean),
        );
    }, [offerIdsRaw]);
    const [offerMetaMap, setOfferMetaMap] = useState({});
    const [sourceCounts, setSourceCounts] = useState({ site_shariti: 0, layan: 0, total: 0 });
    const pepitesOnly = searchParams.get("pepites") === "1";
    // iter95 · CEO 2026-09-01 · "smart" is the new default sort combining
    // ai_score + recency (see backend). Legacy "score" param still works
    // for URL back-compat but maps to the same aggregation.
    const sortBy = searchParams.get("sort") || "smart";

    const [offerOptions, setOfferOptions] = useState([]);

    function updateParams(mutator) {
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev);
            mutator(next);
            return next;
        }, { replace: true });
    }

    function setPseudoFilter(key) {
        updateParams((next) => {
            if (!key) next.delete("status");
            else next.set("status", key);
        });
    }

    function toggleStatus(key) {
        updateParams((next) => {
            const current = parseSetParam(next.get("status") || "");
            // If a pseudo is currently set, clicking a real status replaces it
            if (next.get("status") === "urgent" || next.get("status") === "to_recall") {
                current.clear();
            }
            if (current.has(key)) current.delete(key);
            else current.add(key);
            setParamFromSet(next, "status", current);
        });
    }

    function clearAllStatuses() {
        updateParams((next) => next.delete("status"));
    }

    function toggleSource(value) {
        updateParams((next) => {
            const current = parseSetParam(next.get("source") || "");
            if (current.has(value)) current.delete(value);
            else current.add(value);
            setParamFromSet(next, "source", current);
        });
    }

    function togglePepitesOnly() {
        updateParams((next) => {
            if (pepitesOnly) next.delete("pepites");
            else next.set("pepites", "1");
        });
    }

    function selectSort(value) {
        updateParams((next) => {
            if (value === "score") next.delete("sort");
            else next.set("sort", value);
        });
    }

    function toggleOffer(value) {
        updateParams((next) => {
            const current = new Set(
                (next.get("offer_id") || "").split(",").map((s) => s.trim()).filter(Boolean),
            );
            const key = String(value);
            if (current.has(key)) current.delete(key);
            else current.add(key);
            if (current.size === 0) next.delete("offer_id");
            else next.set("offer_id", Array.from(current).join(","));
        });
    }

    function clearOffers() {
        updateParams((next) => next.delete("offer_id"));
    }

    // Keep search input in sync with URL on external updates (topbar search,
    // KPI tiles…)
    useEffect(() => {
        const urlQ = searchParams.get("q") || "";
        if (urlQ !== search) setSearch(urlQ);
        const newScopeActive = searchParams.get("scope") !== "all";
        if (newScopeActive !== scopeActive) setScopeActive(newScopeActive);
        const newShowTest = searchParams.get("show_test") === "1";
        if (newShowTest !== showTest) setShowTest(newShowTest);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);

    // Persist scope + search + show_test to URL
    useEffect(() => {
        updateParams((next) => {
            if (search) next.set("q", search);
            else next.delete("q");
            if (!scopeActive) next.set("scope", "all");
            else next.delete("scope");
            if (showTest) next.set("show_test", "1");
            else next.delete("show_test");
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search, scopeActive, showTest]);

    // Load offers once
    useEffect(() => {
        let active = true;
        async function loadOffers() {
            try {
                const { data } = await api.get("/integrations/layan/offers");
                if (!active) return;
                setOfferOptions(
                    data
                        .filter((o) => o.imported_count > 0)
                        .sort((a, b) => (b.imported_count || 0) - (a.imported_count || 0)),
                );
            } catch {
                if (active) setOfferOptions([]);
            }
        }
        loadOffers();
        return () => { active = false; };
    }, []);

    // Resolve current selected offers' meta (in iteration order of the URL)
    useEffect(() => {
        if (offerIds.size === 0) {
            setOfferMetaMap({});
            return;
        }
        const map = {};
        offerOptions.forEach((o) => {
            const key = String(o.layan_offer_id);
            if (offerIds.has(key)) map[key] = o;
        });
        setOfferMetaMap(map);
    }, [offerIds, offerOptions]);

    const selectedOffersMeta = useMemo(
        () => Array.from(offerIds).map((id) => offerMetaMap[id]).filter(Boolean),
        [offerIds, offerMetaMap],
    );

    // === DATA FETCH ===
    useEffect(() => {
        // iter95 · Fix flicker "Gillian apparaît puis disparaît" — quand
        // l'utilisateur tape un nouveau terme, le `searched` memo re-filtre
        // instantanément la LISTE PRÉCÉDENTE (500 pépites d'avant) et
        // affiche brièvement des résultats stale (ex : Gillian si elle
        // était dans la liste). Puis le nouveau fetch termine avec 0
        // (backend prod avant iter94) ou avec Gillian (backend preview
        // après iter94). Dans les 2 cas c'est un flash désagréable.
        // Solution : vider la liste dès qu'on lance un fetch. Un
        // skeleton "Loading" apparaît proprement à la place.
        setList([]);
        async function fetchData() {
            setLoading(true);
            try {
                const params = {};
                const isSearching = (search || "").trim().length > 0;

                // When searching from the topbar, the search MUST be global:
                // the user typed a name and expects to find ANY candidate
                // matching it, regardless of which saved view / offer / pool /
                // status filter is currently active. We drop every narrowing
                // filter (status, offer, source, scope, ai_score_min, pool,
                // set_aside) so the backend only applies RBAC scope (what
                // this user is allowed to see). Client-side `searched` then
                // narrows down by name/city/position/phone.
                if (!isSearching) {
                    if (pseudoFilter === "urgent") {
                        // iter96 · CEO 2026-09-01 (2e frustration) : la file
                        // d'appel doit prioriser correctement les candidats.
                        // Ancien : on ne passait aucun status au backend, on
                        // récupérait 500 candidats mixed puis on filtrait
                        // client-side sur new/prerec_assigned. Résultat :
                        // si le top-500 par smart-sort contenait plus de
                        // 500 candidats hors-scope, on ratait des urgents.
                        // Fix : passer directement status=new,prerec_assigned
                        // à l'API — le backend garantit alors 500 candidats
                        // urgents, tous smart-sorted.
                        params.status = "new,prerec_assigned";
                    } else if (pseudoFilter === "to_recall") {
                        params.to_recall = true;
                    } else if (statusValues.size > 0) {
                        params.status = Array.from(statusValues).join(",");
                    }

                    if (offerIds.size > 0) params.offer_id = Array.from(offerIds).join(",");
                    else if (
                        scopeActive
                        && !statusValues.has("rejected")
                        && !statusValues.has("hired")
                    ) params.scope = "active";

                    if (sourceValues.size > 0) params.source = Array.from(sourceValues).join(",");

                    if (kind === "pepites" || pepitesOnly) params.ai_score_min = 82;
                } else {
                    // SEARCHING: include vivier + set-aside + rejected so a name
                    // match surfaces no matter where the candidate sits in the
                    // funnel. RBAC scope still applies (a pre-recruteur won't
                    // see a hired candidate from a different funnel).
                    params.pool = "all";
                    params.set_aside = "all";
                    // ALWAYS forward the search token to the backend. The
                    // backend routes it to a matricule regex when it looks
                    // like a code (AT0272 / 0272) OR to a name/email/phone/
                    // city regex otherwise. This is what keeps a
                    // low-ai-score candidate reachable — including one
                    // wrongly auto-flagged is_test — without depending on
                    // the 500-doc client-side window.
                    params.q = search.trim();
                }
                if (sortBy === "recent") params.sort = "recent";
                else if (sortBy === "oldest") params.sort = "oldest";
                else if (sortBy === "score") params.sort = "score";  // legacy → keep backend default
                else params.sort = "smart";  // iter95 · new default

                // is_test candidates are hidden from the list by default.
                // An admin sees them when either the checkbox is ON or a
                // search is active (so a false-positive auto-flag stays
                // reachable by name). Non-admins never see them (server
                // ignores this flag for them — belt-and-braces).
                if (isAdmin && (showTest || isSearching)) {
                    params.include_test = "true";
                }

                if (!isSearching && pseudoFilter === "urgent") {
                    // iter96 · Le backend a déjà filtré sur new+prerec_assigned
                    // ET trié en smart. Le filtre client-side devient un
                    // no-op défensif (au cas où le backend retournerait
                    // autre chose — jamais observé).
                    const { data } = await api.get("/candidates", { params });
                    setList(data);
                } else {
                    const { data } = await api.get("/candidates", { params });
                    let result = data;
                    if (kind === "alertes") {
                        result = result.filter((c) => c.risk_level === "high" || (c.ai_score || 0) < 50);
                    }
                    setList(result);
                }
            } catch (e) {
                console.error("Failed to load candidates:", e);
                setList([]);
            } finally {
                setLoading(false);
            }
        }
        fetchData();
    }, [pseudoFilter, statusValues, sourceValues, scopeActive, offerIds, search, kind, pepitesOnly, sortBy, isAdmin, showTest]);

    // === SOURCE COUNTS ===
    useEffect(() => {
        async function fetchCounts() {
            try {
                const params = {};
                const isSearching = (search || "").trim().length > 0;
                // Mirror the data-fetch behavior: when searching, the counts
                // must reflect the GLOBAL scope (otherwise the "Toutes (N)"
                // pill lies and the user thinks the search found nothing).
                if (!isSearching) {
                    if (pseudoFilter === "urgent") {
                        // counts for urgent: rely on backend without status filter
                    } else if (pseudoFilter === "to_recall") {
                        params.to_recall = true;
                    } else if (statusValues.size > 0) {
                        params.status = Array.from(statusValues).join(",");
                    }
                    if (offerIds.size > 0) params.offer_id = Array.from(offerIds).join(",");
                    else if (
                        scopeActive
                        && !statusValues.has("rejected")
                        && !statusValues.has("hired")
                    ) params.scope = "active";
                    if (kind === "pepites" || pepitesOnly) params.ai_score_min = 82;
                } else {
                    params.pool = "all";
                    params.set_aside = "all";
                }
                const { data } = await api.get("/candidates/source-counts", { params });
                setSourceCounts(data || { site_shariti: 0, layan: 0, total: 0 });
            } catch (_e) {
                setSourceCounts({ site_shariti: 0, layan: 0, total: 0 });
            }
        }
        fetchCounts();
    }, [pseudoFilter, statusValues, scopeActive, offerIds, search, kind, pepitesOnly]);

    // === CLIENT-SIDE SEARCH ===
    // Matches name, city, position, phone AND email — so a recruiter can
    // paste an email from a notification and immediately find the candidate.
    const searched = useMemo(() => {
        if (!search) return list;
        const q = search.toLowerCase().trim();
        return list.filter(
            (c) =>
                `${c.first_name} ${c.last_name}`.toLowerCase().includes(q) ||
                c.city?.toLowerCase().includes(q) ||
                c.position?.toLowerCase().includes(q) ||
                c.phone?.toLowerCase().includes(q) ||
                c.email?.toLowerCase().includes(q) ||
                // Matricule (code RD) — case-insensitive so both "at0272"
                // and "AT0272" match. Fulfils the "chercher par code"
                // requirement from the CEO brief 2026-07-27.
                (c.code_rd || "").toLowerCase().includes(q),
        );
    }, [list, search]);

    // === SPLIT "TO PROCESS" vs "DONE" buckets per role ===
    // The queue saved to localStorage (used by prev/next navigation in the
    // candidate profile) must contain ONLY candidates the current user
    // still has to handle, otherwise clicking Next jumps over candidates
    // who already had their immersion scheduled.
    const isReferent = hasRole(user, "referent");
    const isPreRec = hasRole(user, "pre_recruteur");
    const toProcessStatuses = useMemo(() => {
        const set = new Set();
        if (isPreRec || isAdmin) { set.add("new"); set.add("prerec_assigned"); }
        if (isReferent || isAdmin) { set.add("immersion_scheduled"); }
        return set;
    }, [isAdmin, isPreRec, isReferent]);

    const { toProcessList, doneList } = useMemo(() => {
        const toProcessList = [];
        const doneList = [];
        searched.forEach((c) => {
            if (toProcessStatuses.has(c.status)) toProcessList.push(c);
            else doneList.push(c);
        });
        return { toProcessList, doneList };
    }, [searched, toProcessStatuses]);

    // If user explicitly filtered on a status (e.g. status=rejected on the
    // Refusés tab) OR picked an explicit sort, disable the split — they want
    // exactly what they asked for, in the order they asked.
    const splitActive = statusValues.size === 0 && !pseudoFilter && sortBy === "score";
    const filtered = useMemo(() => {
        if (!splitActive) return searched;
        return showDone ? [...toProcessList, ...doneList] : toProcessList;
    }, [splitActive, searched, showDone, toProcessList, doneList]);

    // === QUEUE PERSISTENCE ===
    // The queue stored in localStorage is what powers the "Précédent / Suivant"
    // navigation in CandidateProfile. It MUST exactly match the visible list,
    // otherwise the recruiter clicks one candidate and ends up navigating to
    // someone outside their filter.
    useEffect(() => {
        if (filtered.length === 0) return;
        const labelParts = [];
        if (kind === "pepites" || pepitesOnly) labelParts.push("Pépites");
        else if (kind === "alertes") labelParts.push("Alertes");
        if (pseudoFilter) labelParts.push(STATUS_LABEL_LONG[pseudoFilter] || pseudoFilter);
        else if (statusValues.size === 1) labelParts.push(STATUS_LABEL_LONG[Array.from(statusValues)[0]] || Array.from(statusValues)[0]);
        else if (statusValues.size > 1) labelParts.push(`${statusValues.size} statuts`);
        if (selectedOffersMeta.length === 1) labelParts.push(selectedOffersMeta[0].title.slice(0, 30));
        else if (selectedOffersMeta.length > 1) labelParts.push(`${selectedOffersMeta.length} offres`);
        if (sourceValues.size === 1) {
            const v = Array.from(sourceValues)[0];
            labelParts.push(v === SOURCES.SITE ? "Site Shariti" : "Layan");
        } else if (sourceValues.size > 1) labelParts.push("Multi-sources");
        if (sortBy !== "score") labelParts.push(sortBy === "recent" ? "Récents" : "Anciens");
        if ((search || "").trim()) labelParts.push(`"${search.trim().slice(0, 20)}"`);
        const queueLabel = labelParts.length ? labelParts.join(" · ") : "Tous les candidats";

        const returnUrl = `/${kind === "pepites" ? "pepites" : kind === "alertes" ? "alertes" : "candidats"}?${searchParams.toString()}`;
        saveQueue({
            ids: filtered.map((c) => c.id),
            label: queueLabel,
            return_url: returnUrl,
        });
    }, [filtered, pseudoFilter, statusValues, selectedOffersMeta, searchParams, kind, sourceValues, search, pepitesOnly, sortBy]);

    const KIND_META = {
        pepites: {
            title: "Pépites",
            icon: "✨",
            subtitle: "Candidats avec un score IA ≥ 82 — les meilleurs profils à appeler en priorité",
        },
        alertes: {
            title: "Alertes",
            icon: "⚠️",
            subtitle: "Candidats à risque (red flags ou score IA < 50) — à examiner",
        },
    };
    const kindMeta = KIND_META[kind];

    // Build short summary string for the collapsed filter panel header
    const filterSummary = useMemo(() => {
        const parts = [];
        if (pseudoFilter) parts.push(STATUS_LABEL_LONG[pseudoFilter]);
        else if (statusValues.size > 0) {
            const labels = Array.from(statusValues)
                .map((k) => STATUS_OPTIONS.find((s) => s.key === k)?.label || k);
            parts.push(labels.length <= 2 ? labels.join(" + ") : `${labels.length} statuts`);
        }
        if (sourceValues.size === 1) {
            const v = Array.from(sourceValues)[0];
            parts.push(v === SOURCES.SITE ? "Site Shariti" : "Layan");
        } else if (sourceValues.size > 1) parts.push("Multi-sources");
        if (selectedOffersMeta.length === 1) parts.push(selectedOffersMeta[0].title.slice(0, 24));
        else if (selectedOffersMeta.length > 1) parts.push(`${selectedOffersMeta.length} offres`);
        if (pepitesOnly && kind !== "pepites") parts.push("✨ Pépites");
        if (sortBy !== "score") parts.push(sortBy === "recent" ? "Récents" : "Anciens");
        if (parts.length === 0) return "Aucun filtre actif";
        return parts.join(" · ");
    }, [pseudoFilter, statusValues, sourceValues, selectedOffersMeta, pepitesOnly, sortBy, kind]);

    // Saved view params capture (everything that defines the filter state)
    const currentSavedParams = useMemo(() => {
        const p = {};
        if (pseudoFilter) p.status = pseudoFilter;
        else if (statusValues.size > 0) p.status = Array.from(statusValues).join(",");
        if (sourceValues.size > 0) p.source = Array.from(sourceValues).join(",");
        if (offerIds.size > 0) p.offer_id = Array.from(offerIds).join(",");
        if (!scopeActive) p.scope = "all";
        if (pepitesOnly) p.pepites = "1";
        if (sortBy !== "score") p.sort = sortBy;
        return p;
    }, [pseudoFilter, statusValues, sourceValues, offerIds, scopeActive, pepitesOnly, sortBy]);

    function applySavedView(params) {
        // Reset everything by rebuilding from scratch
        const next = new URLSearchParams();
        // Preserve q (search) — user might want to combine with their search
        if (search) next.set("q", search);
        Object.entries(params || {}).forEach(([k, v]) => {
            if (v !== null && v !== undefined && v !== "") next.set(k, String(v));
        });
        setSearchParams(next, { replace: true });
    }

    function viewMatchesCurrent(viewParams) {
        // Compare key-by-key; missing key counts as empty string
        const keys = new Set([
            ...Object.keys(viewParams || {}),
            ...Object.keys(currentSavedParams),
        ]);
        for (const k of keys) {
            if (String(viewParams?.[k] ?? "") !== String(currentSavedParams[k] ?? "")) return false;
        }
        return true;
    }

    // Resolve current scope for SavedViews (candidats / pepites / alertes)
    const savedScope = kind === "pepites" ? "pepites" : kind === "alertes" ? "alertes" : "candidats";
    const filterStorageKey = `${kind}-filters`;

    return (
        <div className="space-y-4 md:space-y-6" data-testid="candidates-page">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div className="min-w-0">
                    {kindMeta && (
                        <div className="inline-flex items-center gap-2 rounded-full bg-shariti-orange-soft text-shariti-orange px-3 py-1 text-[10px] font-bold uppercase tracking-widest mb-2">
                            <span>{kindMeta.icon}</span> {kindMeta.title}
                        </div>
                    )}
                    <h1 className="text-3xl md:text-4xl font-black tracking-tight text-shariti-black">
                        {kindMeta?.title || STATUS_LABEL_LONG[pseudoFilter] || (statusValues.size === 1 ? STATUS_LABEL_LONG[Array.from(statusValues)[0]] : "Candidats")}
                    </h1>
                    <p className="mt-1 text-sm font-medium text-slate-500">
                        {kindMeta ? (
                            <>{kindMeta.subtitle} · <strong>{filtered.length}</strong> profil{filtered.length > 1 ? "s" : ""}</>
                        ) : (
                            <>{filtered.length} profil{filtered.length > 1 ? "s" : ""} · triés par {
                                sortBy === "score" ? "score" :
                                sortBy === "recent" ? "date (récents)" :
                                sortBy === "oldest" ? "date (anciens)" :
                                "score & récence"
                            }{offerIds.size > 1 ? (
                                <> · <strong className="text-shariti-orange">{offerIds.size} offres combinées</strong></>
                            ) : offerIds.size === 1 ? (
                                <> · 1 offre filtrée</>
                            ) : (scopeActive ? " · offres actives" : " · toutes les offres")}</>
                        )}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 bg-white border border-shariti-gray rounded-full px-4 h-11 text-sm w-full md:w-80">
                        <Search className="h-4 w-4 text-slate-400" />
                        <input
                            data-testid="candidates-search"
                            placeholder="Nom, téléphone, ville, poste…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="bg-transparent outline-none flex-1 text-shariti-black font-medium"
                        />
                    </div>
                </div>
            </div>

            {selectedOffersMeta.length > 0 && (
                <div
                    className={`card-soft p-3 md:p-4 space-y-2 ${
                        selectedOffersMeta.length > 1
                            ? "ring-2 ring-shariti-orange/40 bg-shariti-orange-soft/30"
                            : ""
                    }`}
                    data-testid="offer-filter-pill"
                >
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="h-8 w-8 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                            <Briefcase className="h-4 w-4" />
                        </div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                            {selectedOffersMeta.length === 1
                                ? "Offre filtrée"
                                : `${selectedOffersMeta.length} offres cumulées → ${filtered.length} candidats affichés`}
                        </div>
                        {selectedOffersMeta.length > 1 && (
                            <div
                                data-testid="offer-multi-warning"
                                className="text-[11px] font-semibold text-shariti-orange"
                            >
                                ⚠ Les candidats de toutes ces offres sont mélangés dans la liste ci-dessous.
                            </div>
                        )}
                        <button
                            onClick={clearOffers}
                            data-testid="clear-offer-filter"
                            className="ml-auto btn-ghost text-xs"
                        >
                            <X className="h-3.5 w-3.5" /> Tout retirer
                        </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {selectedOffersMeta.map((meta) => (
                            <div
                                key={meta.layan_offer_id}
                                data-testid={`offer-pill-${meta.layan_offer_id}`}
                                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-shariti-orange/40 text-xs max-w-full"
                            >
                                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 shrink-0">
                                    #{meta.layan_offer_id}
                                </span>
                                <span className="font-extrabold text-shariti-black truncate" title={meta.title}>
                                    {meta.title}
                                </span>
                                {typeof meta.imported_count === "number" && (
                                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-shariti-orange-soft text-shariti-orange shrink-0">
                                        {meta.imported_count}
                                    </span>
                                )}
                                <span
                                    className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${
                                        OFFER_STATUS_TONE[meta.status] || "bg-slate-100 text-slate-600 border-slate-200"
                                    }`}
                                >
                                    {meta.status}
                                </span>
                                <button
                                    onClick={() => toggleOffer(meta.layan_offer_id)}
                                    data-testid={`offer-pill-remove-${meta.layan_offer_id}`}
                                    className="h-5 w-5 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-shariti-red hover:bg-red-50 transition shrink-0"
                                    title="Retirer cette offre"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* === SAVED VIEWS BAR — always visible, above filters === */}
            <SavedViewsBar
                scope={savedScope}
                currentParams={currentSavedParams}
                onApply={applySavedView}
                activeMatch={viewMatchesCurrent}
            />

            {/* === COLLAPSIBLE FILTERS PANEL === */}
            <CollapsibleFilters storageKey={filterStorageKey} summary={filterSummary} defaultOpen>
                {/* Offer chips — multi-select. When scope=active is on, archived/draft offers are hidden.
                    CEO 2026-09-29 fix : les offres ACTIVEMENT filtrées sont
                    TOUJOURS visibles en tant que chip surligné, même quand
                    elles ne sont pas dans le top-4 par volume. Sans ce fix,
                    un filtre pouvait rester actif tout en étant "planqué"
                    dans le dropdown "+ Autres offres", trompant l'utilisateur
                    sur le nombre réel de candidats affichés. */}
                {offerOptions.length > 0 && (() => {
                    const eligible = offerOptions.filter((o) => {
                        if (!scopeActive) return true;
                        return o.status === "Publiée";
                    });
                    if (eligible.length === 0 && offerIds.size === 0) return null;

                    // Toujours faire remonter en priorité les offres actives,
                    // puis compléter jusqu'à 4 avec les autres offres éligibles
                    // triées par volume (déjà trié depuis loadOffers).
                    const activeInEligible = eligible.filter((o) =>
                        offerIds.has(String(o.layan_offer_id)),
                    );
                    // Offres actives dont la meta a été résolue MAIS qui
                    // ont été exclues de "eligible" par le filtre scopeActive
                    // (offre archivée / brouillon). On les rajoute quand
                    // même dans les chips visibles pour que l'utilisateur
                    // puisse les retirer d'un clic.
                    const activeArchived = Array.from(offerIds)
                        .filter((id) => !activeInEligible.some((o) => String(o.layan_offer_id) === id))
                        .map((id) => offerMetaMap[id])
                        .filter(Boolean);
                    const activeChips = [...activeInEligible, ...activeArchived];
                    const inactiveEligible = eligible.filter(
                        (o) => !offerIds.has(String(o.layan_offer_id)),
                    );
                    // Chips visibles = toutes les actives + suffisamment
                    // d'inactives pour atteindre 4 mini (mais on peut dépasser
                    // 4 si plusieurs sont actives).
                    const remainingSlots = Math.max(0, 4 - activeChips.length);
                    const visibleChips = [
                        ...activeChips,
                        ...inactiveEligible.slice(0, remainingSlots),
                    ];
                    const hiddenChips = inactiveEligible.slice(remainingSlots);

                    return (
                        <div
                            className="flex flex-wrap items-center gap-2"
                            data-testid="offer-filter-chips"
                        >
                            <Briefcase className="h-4 w-4 text-slate-400 shrink-0" />
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mr-1">
                                Par offre :
                            </span>
                            {visibleChips.map((o) => {
                                const key = String(o.layan_offer_id);
                                const isActive = offerIds.has(key);
                                const regionSuffix = o.region ? ` · ${o.region}` : (o.city ? ` · ${o.city}` : "");
                                return (
                                    <button
                                        key={o.layan_offer_id}
                                        onClick={() => toggleOffer(o.layan_offer_id)}
                                        data-testid={`offer-chip-${o.layan_offer_id}`}
                                        className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-bold transition max-w-[400px] border ${
                                            isActive
                                                ? "bg-shariti-orange text-white border-shariti-orange"
                                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                        }`}
                                        title={`${o.title}${regionSuffix}`}
                                    >
                                        {isActive && <span className="text-[10px]">✓</span>}
                                        <span className="truncate">{o.title}{regionSuffix}</span>
                                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] shrink-0 ${
                                            isActive ? "bg-white/20" : "bg-shariti-orange-soft text-shariti-orange"
                                        }`}>
                                            {o.imported_count}
                                        </span>
                                        {isActive && (
                                            <span
                                                className="text-[10px] opacity-80 shrink-0"
                                                aria-hidden="true"
                                            >
                                                ✕
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                            {hiddenChips.length > 0 && (
                                <select
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val) toggleOffer(val);
                                        e.target.value = "";
                                    }}
                                    data-testid="offer-select-more"
                                    defaultValue=""
                                    className="h-8 px-3 rounded-full text-xs font-bold border border-shariti-gray bg-white text-slate-600 cursor-pointer hover:border-shariti-orange hover:text-shariti-orange"
                                >
                                    <option value="" disabled>
                                        + Autres offres ({hiddenChips.length})…
                                    </option>
                                    {hiddenChips.map((o) => (
                                        <option key={o.layan_offer_id} value={o.layan_offer_id}>
                                            {o.title}{o.region ? ` · ${o.region}` : ""} ({o.imported_count})
                                        </option>
                                    ))}
                                </select>
                            )}
                            {activeChips.length > 1 && (
                                <button
                                    type="button"
                                    onClick={clearOffers}
                                    data-testid="offer-clear-all"
                                    title="Retirer toutes les offres filtrées"
                                    className="inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-bold border border-shariti-red/40 bg-red-50 text-shariti-red hover:bg-red-100 transition"
                                >
                                    <X className="h-3 w-3" />
                                    Tout retirer ({activeChips.length})
                                </button>
                            )}
                            {!scopeActive && (
                                <span className="text-[10px] italic text-slate-400">
                                    inclut brouillons & archivées
                                </span>
                            )}
                        </div>
                    );
                })()}

                {/* Source multi-select chips */}
                <div className="flex flex-wrap items-center gap-2" data-testid="source-filters">
                    <Workflow className="h-4 w-4 text-slate-400 shrink-0" />
                    <span className="text-xs font-bold uppercase tracking-widest text-slate-400 mr-1">
                        Origine :
                    </span>
                    <button
                        data-testid="source-chip-all"
                        onClick={() => updateParams((next) => next.delete("source"))}
                        className={`px-3 h-8 rounded-full text-xs font-bold border transition inline-flex items-center gap-1.5 ${
                            sourceValues.size === 0
                                ? "bg-shariti-black text-white border-shariti-black"
                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-black"
                        }`}
                    >
                        Toutes
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            sourceValues.size === 0 ? "bg-white/20" : "bg-slate-100 text-slate-500"
                        }`}>
                            {sourceCounts.total}
                        </span>
                    </button>
                    <button
                        data-testid="source-chip-site"
                        onClick={() => toggleSource(SOURCES.SITE)}
                        className={`px-3 h-8 rounded-full text-xs font-bold border transition inline-flex items-center gap-1.5 ${
                            sourceValues.has(SOURCES.SITE)
                                ? "bg-shariti-orange text-white border-shariti-orange"
                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        <Globe className="h-3 w-3" />
                        Site Shariti
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            sourceValues.has(SOURCES.SITE) ? "bg-white/20" : "bg-shariti-orange-soft text-shariti-orange"
                        }`}>
                            {sourceCounts.site_shariti}
                        </span>
                    </button>
                    <button
                        data-testid="source-chip-layan"
                        onClick={() => toggleSource(SOURCES.LAYAN)}
                        className={`px-3 h-8 rounded-full text-xs font-bold border transition inline-flex items-center gap-1.5 ${
                            sourceValues.has(SOURCES.LAYAN)
                                ? "bg-shariti-orange text-white border-shariti-orange"
                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        <Briefcase className="h-3 w-3" />
                        Layan / job-boards
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${
                            sourceValues.has(SOURCES.LAYAN) ? "bg-white/20" : "bg-shariti-orange-soft text-shariti-orange"
                        }`}>
                            {sourceCounts.layan}
                        </span>
                    </button>
                </div>

                {/* Status filters — multi-select for real statuses, exclusive pseudo */}
                <div className="flex flex-wrap items-center gap-2" data-testid="candidates-filters">
                    <Filter className="h-4 w-4 text-slate-400 shrink-0" />
                    <button
                        data-testid="filter-all"
                        onClick={clearAllStatuses}
                        className={`px-4 h-9 rounded-full text-xs font-bold border transition whitespace-nowrap ${
                            !pseudoFilter && statusValues.size === 0
                                ? "bg-shariti-orange text-white border-shariti-orange"
                                : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                        }`}
                    >
                        Tous
                    </button>
                    {PSEUDO_FILTERS.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`filter-${f.key}`}
                            onClick={() => setPseudoFilter(pseudoFilter === f.key ? "" : f.key)}
                            className={`px-4 h-9 rounded-full text-xs font-bold border transition whitespace-nowrap ${
                                pseudoFilter === f.key
                                    ? "bg-shariti-orange text-white border-shariti-orange"
                                    : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300 mx-1">|</span>
                    {STATUS_OPTIONS.map((f) => {
                        const isActive = statusValues.has(f.key) && !pseudoFilter;
                        return (
                            <button
                                key={f.key}
                                data-testid={`filter-${f.key}`}
                                onClick={() => toggleStatus(f.key)}
                                className={`px-4 h-9 rounded-full text-xs font-bold border transition whitespace-nowrap inline-flex items-center gap-1.5 ${
                                    isActive
                                        ? "bg-shariti-orange text-white border-shariti-orange"
                                        : "bg-white text-slate-600 border-shariti-gray hover:border-shariti-orange hover:text-shariti-orange"
                                }`}
                            >
                                {isActive && <span className="text-[10px]">✓</span>}
                                {f.label}
                            </button>
                        );
                    })}
                    {offerIds.size === 0 && !statusValues.has("rejected") && !statusValues.has("hired") && (
                        <label className="ml-auto inline-flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer pl-3 border-l border-shariti-gray">
                            <input
                                type="checkbox"
                                checked={scopeActive}
                                onChange={(e) => setScopeActive(e.target.checked)}
                                data-testid="scope-toggle"
                                className="h-4 w-4 rounded border-shariti-gray accent-shariti-orange"
                            />
                            Offres actives uniquement
                        </label>
                    )}
                    {offerIds.size === 0 && (statusValues.has("rejected") || statusValues.has("hired")) && (
                        <span className="ml-auto text-[11px] font-medium italic text-slate-400 pl-3 border-l border-shariti-gray" title="Sur les statuts terminaux, le filtre 'offres actives' est ignoré pour ne pas masquer d'historique">
                            Toutes offres incluses
                        </span>
                    )}
                    {isAdmin && (
                        <label
                            className="inline-flex items-center gap-2 text-xs font-bold text-slate-600 cursor-pointer pl-3 border-l border-shariti-gray"
                            title="Admin uniquement — inclut les candidats marqués comme test (utile pour retrouver un faux positif de l'auto-flag)"
                        >
                            <input
                                type="checkbox"
                                checked={showTest}
                                onChange={(e) => setShowTest(e.target.checked)}
                                data-testid="show-test-toggle"
                                className="h-4 w-4 rounded border-shariti-gray accent-shariti-orange"
                            />
                            Afficher les candidats test
                        </label>
                    )}
                </div>

                {/* Pépites toggle + Sort selector — available on ALL kinds */}
                <div className="flex flex-wrap items-center gap-3">
                    {kind !== "pepites" && kind !== "alertes" && (
                        <button
                            onClick={togglePepitesOnly}
                            data-testid="toggle-pepites-only"
                            className={`px-3 h-8 rounded-full text-xs font-bold border transition inline-flex items-center gap-1.5 ${
                                pepitesOnly
                                    ? "bg-amber-100 text-amber-800 border-amber-300"
                                    : "bg-white text-slate-600 border-shariti-gray hover:border-amber-300 hover:text-amber-700"
                            }`}
                        >
                            <span>✨</span>
                            Pépites uniquement (score IA ≥ 82)
                        </button>
                    )}

                    <div className="ml-auto inline-flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Trier :</span>
                        <select
                            value={sortBy}
                            onChange={(e) => selectSort(e.target.value)}
                            data-testid="candidates-sort"
                            className="h-8 px-3 rounded-full text-xs font-bold border border-shariti-gray bg-white text-slate-700 hover:border-shariti-orange focus:border-shariti-orange focus:outline-none transition"
                        >
                            <option value="smart">Score & récence combinés</option>
                            <option value="score">Meilleur score IA</option>
                            <option value="recent">Plus récents d'abord</option>
                            <option value="oldest">Plus anciens d'abord</option>
                        </select>
                    </div>
                </div>
            </CollapsibleFilters>

            {loading ? (
                <div className="grid gap-4">
                    {[1, 2, 3].map((i) => (
                        <div
                            key={i}
                            className="card-soft p-6 animate-pulse h-32 bg-shariti-cream"
                        />
                    ))}
                </div>
            ) : (
                <div className="grid gap-4">
                    {/* Toolbar visible whenever the list has candidates. Groups the
                        smart "À traiter / Traités" split (pre-rec context) with
                        the always-available "Sélection groupée" toggle used by
                        the bulk vivier + bulk badges flows (iter110d). */}
                    {filtered.length > 0 && (
                        <div className="flex items-center justify-between flex-wrap gap-2 mb-1 px-1">
                            <div className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-widest">
                                {splitActive && toProcessList.length > 0 ? (
                                    <>
                                        <span className="h-2 w-2 rounded-full bg-shariti-orange" />
                                        <span className="text-shariti-orange">À traiter ({toProcessList.length})</span>
                                    </>
                                ) : (
                                    <span className="text-slate-500">{filtered.length} candidat{filtered.length > 1 ? "s" : ""} affiché{filtered.length > 1 ? "s" : ""}</span>
                                )}
                            </div>
                            <div className="inline-flex items-center gap-3 flex-wrap">
                                {/* Sélection groupée toggle — when ON, each card
                                    shows a checkbox and a sticky bar lets us
                                    "Mettre N candidats au vivier" ou télécharger
                                    les badges (iter110d) en un shot. */}
                                <button
                                    onClick={toggleSelectMode}
                                    data-testid="toggle-select-mode"
                                    className={`inline-flex items-center gap-1.5 text-[11px] font-bold transition ${selectMode ? "text-shariti-orange" : "text-slate-500 hover:text-shariti-orange"}`}
                                >
                                    {selectMode ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
                                    {selectMode ? "Sélection ON" : "Sélection groupée"}
                                </button>
                                {selectMode && filtered.length > 0 && (
                                    <button
                                        onClick={() => {
                                            const allIds = filtered.map((c) => c.id);
                                            const allSelected = allIds.every((id) => selectedIds.has(id));
                                            if (allSelected) clearSelection();
                                            else setSelectedIds(new Set(allIds));
                                        }}
                                        data-testid="toggle-select-all"
                                        className="text-[11px] font-bold text-shariti-orange hover:underline"
                                    >
                                        {filtered.every((c) => selectedIds.has(c.id))
                                            ? "Tout désélectionner"
                                            : "Tout sélectionner"}
                                    </button>
                                )}
                                {doneList.length > 0 && splitActive && (
                                    <button
                                        onClick={() => {
                                            const next = !showDone;
                                            setShowDone(next);
                                            // When user reveals the done bucket, smoothly scroll to
                                            // the divider so they SEE candidates appear instead of
                                            // wondering if the click did anything.
                                            if (next) {
                                                setTimeout(() => {
                                                    const el = document.querySelector('[data-testid="done-divider"]');
                                                    el?.scrollIntoView({ behavior: "smooth", block: "start" });
                                                }, 80);
                                            }
                                        }}
                                        data-testid="toggle-show-done"
                                        className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-shariti-orange transition"
                                    >
                                        <EyeOff className="h-3.5 w-3.5" />
                                        {showDone
                                            ? `Masquer les ${doneList.length} déjà traités`
                                            : `Voir les ${doneList.length} déjà traités`}
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {filtered.map((c, i) => {
                        // Insert a visual divider between the to-process bucket
                        // and the done bucket when showDone is active.
                        const isFirstDone = splitActive && showDone
                            && i === toProcessList.length
                            && doneList.length > 0
                            && toProcessList.length > 0;
                        return (
                            <div key={c.id}>
                                {isFirstDone && (
                                    <div className="flex items-center gap-3 my-2" data-testid="done-divider">
                                        <div className="flex-1 h-px bg-shariti-gray" />
                                        <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                                            Déjà traités ({doneList.length})
                                        </span>
                                        <div className="flex-1 h-px bg-shariti-gray" />
                                    </div>
                                )}
                                <CandidateCard
                                    candidate={c}
                                    index={i}
                                    selectable={selectMode}
                                    selected={selectedIds.has(c.id)}
                                    onToggleSelect={toggleOne}
                                    onChange={({ id: changedId, removed }) => {
                                        if (removed) {
                                            setList((prev) => prev.filter((x) => x.id !== changedId));
                                            // Drop from selection too — stale ID would re-appear
                                            // in the bulk bar count.
                                            setSelectedIds((prev) => {
                                                if (!prev.has(changedId)) return prev;
                                                const next = new Set(prev);
                                                next.delete(changedId);
                                                return next;
                                            });
                                        }
                                    }}
                                />
                            </div>
                        );
                    })}
                    {filtered.length === 0 && (
                        <div className="card-soft p-8 md:p-10 text-center space-y-4">
                            <div>
                                <p className="font-extrabold text-shariti-black text-base">
                                    Aucun candidat trouvé{search ? <> pour <span className="text-shariti-orange">«&nbsp;{search}&nbsp;»</span></> : ""}
                                </p>
                                {(search || scopeActive || offerIds.size > 0 || sourceValues.size > 0 || statusValues.size > 0 || pepitesOnly) && (
                                    <p className="mt-2 text-xs font-medium text-slate-500 leading-relaxed max-w-md mx-auto">
                                        {search ? (
                                            user?.assigned_offer_ids?.length > 0 ? (
                                                <>La recherche par nom est <strong className="text-shariti-black">globale</strong> (toutes vos offres confondues). Votre compte est restreint à <strong className="text-shariti-orange">{user.assigned_offer_ids.length} offre{user.assigned_offer_ids.length > 1 ? "s" : ""}</strong>. Si la personne est sur une autre offre (ex&nbsp;: une annonce fraîchement créée par l&apos;admin), <strong>demandez à l&apos;admin d&apos;ajouter cette offre à votre périmètre</strong>.</>
                                            ) : (
                                                <>La recherche par nom est <strong className="text-shariti-black">globale</strong> (toutes offres confondues). Si la personne n&apos;apparaît toujours pas, vérifiez sur Layan que le candidat a bien postulé sur une offre <strong>Publiée</strong>, puis relancez une synchronisation.</>
                                            )
                                        ) : (
                                            <>Vos filtres restreignent peut-être trop les résultats. Réinitialisez pour voir tout votre périmètre.</>
                                        )}
                                    </p>
                                )}
                            </div>

                            {/* Surface the active filters so the user knows
                                exactly what's narrowing the result set. */}
                            {(scopeActive || offerIds.size > 0 || sourceValues.size > 0 || statusValues.size > 0 || pepitesOnly) && (
                                <div className="flex items-center justify-center flex-wrap gap-1.5 max-w-lg mx-auto">
                                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 mr-1">Filtres actifs :</span>
                                    {scopeActive && !search && (
                                        <span className="px-2 py-1 rounded-full bg-shariti-cream text-[11px] font-bold text-slate-600">Offres actives uniquement</span>
                                    )}
                                    {offerIds.size > 0 && (
                                        <span className="px-2 py-1 rounded-full bg-shariti-cream text-[11px] font-bold text-slate-600">{offerIds.size} offre{offerIds.size > 1 ? "s" : ""}</span>
                                    )}
                                    {sourceValues.size > 0 && (
                                        <span className="px-2 py-1 rounded-full bg-shariti-cream text-[11px] font-bold text-slate-600">{Array.from(sourceValues).join(", ")}</span>
                                    )}
                                    {statusValues.size > 0 && (
                                        <span className="px-2 py-1 rounded-full bg-shariti-cream text-[11px] font-bold text-slate-600">{statusValues.size} statut{statusValues.size > 1 ? "s" : ""}</span>
                                    )}
                                    {pepitesOnly && (
                                        <span className="px-2 py-1 rounded-full bg-shariti-cream text-[11px] font-bold text-slate-600">Pépites ≥ 82</span>
                                    )}
                                </div>
                            )}

                            <div className="flex flex-wrap items-center justify-center gap-2">
                                {(scopeActive || offerIds.size > 0 || sourceValues.size > 0 || statusValues.size > 0 || pepitesOnly) && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // Reset every narrowing filter so the user sees
                                            // their full RBAC-allowed scope. State here is
                                            // derived from URL search params, so we clear
                                            // the URL — React will then re-derive empty sets.
                                            const p = new URLSearchParams(searchParams);
                                            p.delete("offer_id");
                                            p.delete("source");
                                            p.delete("status");
                                            p.delete("ai_score_min");
                                            p.delete("filter");
                                            p.set("scope", "all");
                                            setSearchParams(p, { replace: true });
                                        }}
                                        data-testid="empty-reset-filters"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white px-4 h-9 font-extrabold text-xs hover:bg-[#d66528] transition shadow-btn"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                        Effacer tous les filtres
                                    </button>
                                )}
                                {splitActive && doneList.length > 0 && !showDone && (
                                    <button
                                        onClick={() => setShowDone(true)}
                                        data-testid="empty-show-done"
                                        className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-gray px-4 h-9 text-xs font-bold text-slate-600 hover:border-shariti-orange hover:text-shariti-orange transition"
                                    >
                                        <EyeOff className="h-3.5 w-3.5" />
                                        Voir les {doneList.length} déjà traités
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Sticky bottom bar — visible only when at least one candidate is
                ticked. Lets the user "Mettre N au vivier" in one shot via
                the shared TalentPoolModal flow. */}
            <BulkActionsBar
                selectedIds={selectedIds}
                onClear={clearSelection}
                onDone={(movedIds) => {
                    // Remove parked candidates from the local list + selection
                    if (Array.isArray(movedIds) && movedIds.length) {
                        setList((prev) => prev.filter((x) => !movedIds.includes(x.id)));
                    }
                    clearSelection();
                }}
            />
        </div>
    );
}
